import { supabase } from '../lib/supabase';

export interface Address {
  id: string;
  user_id: string;
  title: string;
  full_name: string;
  company?: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone?: string;
  latitude?: number;
  longitude?: number;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export class AddressService {
  static async getUserAddresses(): Promise<Address[]> {
    try {
      const { data, error } = await supabase
        .from('addresses')
        .select('*')
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching addresses:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserAddresses:', error);
      throw error;
    }
  }

  static async addAddress(address: Omit<Address, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<Address> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('addresses')
        .insert({
          ...address,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding address:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in addAddress:', error);
      throw error;
    }
  }

  static async updateAddress(id: string, address: Partial<Address>): Promise<Address> {
    try {
      const { data, error } = await supabase
        .from('addresses')
        .update(address)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating address:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updateAddress:', error);
      throw error;
    }
  }

  static async deleteAddress(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('addresses')
        .update({ is_active: false })
        .eq('id', id);

      if (error) {
        console.error('Error deleting address:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteAddress:', error);
      throw error;
    }
  }

  static async setDefaultAddress(id: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // First, unset all other default addresses for this user
      await supabase
        .from('addresses')
        .update({ is_default: false })
        .eq('user_id', user.id);

      // Then set the selected address as default
      const { error } = await supabase
        .from('addresses')
        .update({ is_default: true })
        .eq('id', id);

      if (error) {
        console.error('Error setting default address:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in setDefaultAddress:', error);
      throw error;
    }
  }

  static async getDefaultAddress(): Promise<Address | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('addresses')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_default', true)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error fetching default address:', error);
        throw error;
      }

      return data || null;
    } catch (error) {
      console.error('Error in getDefaultAddress:', error);
      throw error;
    }
  }
}
